import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, viewChild } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ISubscriptionPaymentTransaction } from '@appShared/interfaces/[Model-based]'
import { CommonService, ToastrType } from '@appShared/services'
import { setErrorMessage } from '@appShared/utils/error-message'
import { AccountDetailStore } from '../account-detail/account-detail.store'
import { PaymentMethodModalComponent } from '../payment-method-modal/payment-method-modal.component'
import { PaymentTransactionsComponent } from '../payment-transactions/payment-transactions.component'
import { PaymentInstrumentDisplayComponent } from '@appShared/components/payment/payment-instrument-display/payment-instrument-display.component'

@Component({
   selector: 'app-subscription-billing',
   templateUrl: './subscription-billing.component.html',
   styleUrls: ['./subscription-billing.component.less'],
   imports:[PaymentTransactionsComponent, PaymentInstrumentDisplayComponent],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionBillingComponent {
   private readonly _store = inject(AccountDetailStore)
   private readonly _modalService = inject(NgbModal)
   private readonly _common = inject(CommonService)
   private readonly _injector = inject(Injector)

   public readonly accountSafeId = input.required<string>()

   /* The partner billed check depends on the account, so hold the dependent sections
      back until the request has settled with a value or an error (a reload keeps the
      prior value, so no flicker there). */
   public readonly accountIsSettled = computed(() => !!this._store.account.value() || !!this._store.accountError())
   public readonly isPartnerBilled = this._store.subscriptionIsPartnerBilled
   public readonly billingFrequencyDescription = this._store.billingFrequencyDescription

   /* Billing resources are owned here and driven off our own accountSafeId input.
      The store creates them (so it can reload them after a mutation) but we derive
      all the presentation state — value / isLoading / error — locally. */
   private readonly _subscriptionAuthorizationsResource =
      this._store.createSubscriptionAuthorizationsResource(this.accountSafeId)
   public readonly subscriptionAuthorizations = computed(() => this._subscriptionAuthorizationsResource.value())
   public readonly subscriptionAuthorizationsIsLoading = computed(() => this._subscriptionAuthorizationsResource.isLoading())
   public readonly subscriptionAuthorizationsError = computed(() => this._subscriptionAuthorizationsResource.error() as HttpErrorResponse | undefined)
   public readonly subscriptionAuthorizationsErrorMessage = computed(() =>
      this.subscriptionAuthorizationsError() ? setErrorMessage(this.subscriptionAuthorizationsError(), 'billing') : null
   )

   private readonly _subscriptionPaymentTransactionsResource =
      this._store.createSubscriptionPaymentTransactionsResource(this.accountSafeId)
   public readonly subscriptionPaymentTransactions = computed(() => this._subscriptionPaymentTransactionsResource.value() || [])
   public readonly subscriptionPaymentTransactionsIsLoading = computed(() => this._subscriptionPaymentTransactionsResource.isLoading())
   public readonly subscriptionPaymentTransactionsError = computed(() => this._subscriptionPaymentTransactionsResource.error() as HttpErrorResponse | undefined)
   public readonly subscriptionPaymentTransactionsErrorMessage = computed(() =>
      this.subscriptionPaymentTransactionsError() ? setErrorMessage(this.subscriptionPaymentTransactionsError(), 'billing history') : null
   )

   public readonly refundIsSubmitting = this._store.refundIsSubmitting
   private readonly _paymentTransactions = viewChild(PaymentTransactionsComponent)

   addNewPaymentMethod(): void {
      // Pass our injector so the modal (created via NgbModal) can reach the
      // route-scoped AccountDetailStore. The modal submits + closes on success;
      // the store has already reloaded the authorizations by then.
      this._modalService.open(PaymentMethodModalComponent, {
         size: 'lg',
         backdrop: 'static',
         injector: this._injector
      })
   }

   async refundPaymentTransaction(transaction: ISubscriptionPaymentTransaction): Promise<void> {
      // The rep has already confirmed inline (payment-transactions component) by
      // the time this fires. The store owns the call + billing-history refresh.
      const result = await this._store.refundPaymentTransaction(transaction)
      this._paymentTransactions()?.closeRefundPrompt()
      if (result.ok) {
         this._common.messageUser('<div class="text-center">Reversal Submitted</div>')
      } else {
         this._common.messageUser(
            result.error ?? 'Unable to submit the reversal. Please try again.',
            'Reversal Failed',
            ToastrType.error
         )
      }
   }
}
